'use client';

import { RANGE_FILTER_SECTIONS, SEARCHABLE_FILTER_SECTIONS } from '@/constants/inventory';
import { NewInventoryFiltersContext } from '@/contexts/NewInventoryFiltersContext';
import { useDebounce } from '@/hooks/useDebounce';
import { useFiltersList } from '@/hooks/useFiltersList';
import { hasFilters, isFilterSection } from '@/tools/inventory';
import { FilterSection, ISectionOption } from '@/types/inventory';
import { faChevronDown, faChevronUp, faSearch, faX } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChangeEvent, Fragment, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { FilterSections, FilterSectionsSectionComponent, FilterSectionsTagComponent } from './FilterSections';
import { InventoryFiltersRange } from './InventoryFiltersRange';
import { getCombinedAndSortedOptions } from './utils';

interface SingleOptionProps {
  section: string;
  option: string;
  toggleOption: (option: string) => void;
  value: string[];
  className?: string;
}

const SingleOption = ({ option, section, toggleOption, value, className }: SingleOptionProps) => (
  <li className={twMerge('flex items-center gap-2', className)} data-testid={`${section}-filter-option`}>
    <input
      type="checkbox"
      className="rounded-sm shrink-0"
      checked={value.includes(option)}
      onChange={() => toggleOption(option)}
      data-testid={`filter-option-${option}`}
    />
    <span>{option}</span>
  </li>
);

interface OptionsSectionContentProps {
  section: string;
  options: ISectionOption[];
  value?: string[];
  toggleOption: (option: string) => void;
}

const OptionsSectionContent = ({ options, section, value = [], toggleOption }: OptionsSectionContentProps) => {
  const [search, setSearch] = useState('');
  const lowerSearch = search.toLowerCase().trim();
  const { tagFilters, toggleTagFilter, baseTagFilters, addFilterOptions, removeFilterOptions } =
    useContext(NewInventoryFiltersContext);

  const tagOptions = baseTagFilters.find((item) => item.name === section)?.values || [];
  const tagValue = tagFilters[section] || [];

  // Combine and sort options, with special handling for RV_TYPE
  const combinedOptions = getCombinedAndSortedOptions(section, options, tagOptions);

  return (
    <>
      {SEARCHABLE_FILTER_SECTIONS.has(section) && (
        <div className="bg-blue-50 rounded relative overflow-hidden">
          <FontAwesomeIcon
            icon={faSearch}
            className="size-3 text-black-350 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
          />
          <input
            type="text"
            className="py-3.5 pl-10 pr-2 size-full placeholder-black-350 text-black-500 border-none bg-transparent text-sm focus:outline-none focus:ring-0 focus:border-none"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid={`filter-search-${section}`}
          />
        </div>
      )}
      <ul className="flex flex-col gap-3.5 pl-1.5 overflow-y-auto max-h-[30rem]">
        {combinedOptions
          .filter((option) => (typeof option === 'string' ? option : option.label).toLowerCase().includes(lowerSearch))
          .reduce((acc, option) => {
            if (typeof option === 'string') {
              acc.push(
                <SingleOption
                  key={option}
                  section={section}
                  option={option}
                  toggleOption={toggleOption}
                  value={value}
                />,
              );
              return acc;
            }

            // Handle tag options
            if ('isTag' in option) {
              acc.push(
                <SingleOption
                  key={option.label}
                  section={section}
                  option={option.label}
                  toggleOption={() => toggleTagFilter(section, option.label)}
                  value={tagValue}
                />,
              );
              return acc;
            }

            // Handle grouped options
            const isAllSubOptionsSelected = option.options.every((subOption) => value.includes(subOption));

            acc.push(
              <Fragment key={option.label}>
                <SingleOption
                  section={section}
                  option={option.label}
                  toggleOption={() => {
                    if (!isFilterSection(section)) {
                      return;
                    }

                    if (isAllSubOptionsSelected) {
                      removeFilterOptions(section, option.options);
                    } else {
                      addFilterOptions(section, option.options);
                    }
                  }}
                  value={isAllSubOptionsSelected ? [option.label] : []}
                />
                {option.options.map((subOption) => (
                  <SingleOption
                    key={subOption}
                    section={section}
                    option={subOption}
                    toggleOption={toggleOption}
                    value={value}
                    className="ml-10"
                  />
                ))}
              </Fragment>,
            );

            return acc;
          }, [] as JSX.Element[])}
      </ul>
    </>
  );
};

interface FilterSectionOptionsContentProps {
  section: FilterSection;
}

const FilterSectionOptionsContent = ({ section }: FilterSectionOptionsContentProps) => {
  const { filters, toggleFilter, getSectionOptions } = useContext(NewInventoryFiltersContext);

  const options = getSectionOptions(section);

  const value = filters[section];

  return (
    <OptionsSectionContent
      section={section}
      options={options}
      value={value instanceof Array ? value : []}
      toggleOption={(option) => toggleFilter(section, option)}
    />
  );
};

const DEFAULT_OPEN_SECTIONS = new Set<string>([
  FilterSection.PROMOTIONS,
  FilterSection.CONDITION,
  FilterSection.RV_TYPE,
]);

interface DesktopFilterSectionProps {
  section: string;
}

const DesktopFilterSection = ({ section, children }: PropsWithChildren<DesktopFilterSectionProps>) => {
  const { filters } = useContext(NewInventoryFiltersContext);
  const [isOpen, setIsOpen] = useState(DEFAULT_OPEN_SECTIONS.has(section) || hasFilters(filters[section]));

  return (
    <div className="flex flex-col gap-5">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        type="button"
        className="flex items-center justify-between pt-3 pb-4 border-b border-black-100"
        data-testid={`filter-section-${section}`}
      >
        <h3 className="text-lg font-medium">{section}</h3>
        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="size-4" />
      </button>

      {isOpen && (
        <div className="flex flex-col gap-4" data-testid={`filter-range-${section}`}>
          {children}
        </div>
      )}
    </div>
  );
};

const SearchInput = () => {
  const { query, setQuery } = useContext(NewInventoryFiltersContext);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = query;
    }
  }, [query]);

  const [onChange] = useDebounce((e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  });

  return (
    <div className="relative">
      <input
        type="text"
        className="rounded bg-black-50 py-3.5 pr-3.5 pl-12 placeholder-black-400 border-none outline-none focus:ring-0 size-full"
        placeholder="Search"
        defaultValue={query}
        onChange={onChange}
        ref={inputRef}
        data-testid="filter-search-desktop"
      />
      <FontAwesomeIcon
        icon={faSearch}
        className="absolute size-4 left-4 top-1/2 -translate-y-1/2 text-black-400 pointer-events-none"
      />
    </div>
  );
};

const FilterList = () => {
  const { clearFilters } = useContext(NewInventoryFiltersContext);
  const { filtersList } = useFiltersList();

  if (filtersList.length === 0) return null;

  return (
    <div className="flex flex-col gap-2.5">
      <ul className="px-3 py-5 flex flex-wrap gap-2.5 rounded-md bg-blue-50 border border-blue-100">
        {filtersList.map((item) => (
          <li
            key={item.key}
            className="bg-white border border-black-200 rounded-md px-2.5 py-2 flex items-center gap-2"
            data-testid={`filter-tag-${item.label}`}
          >
            <span className="text-sm text-blue-700 font-medium">{item.label}</span>
            <button type="button" onClick={item.onRemove} data-testid={`close-filter-tag-${item.label}`}>
              <FontAwesomeIcon icon={faX} className="size-3 text-black-400" />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={clearFilters}
        className="bg-white border border-black-500 rounded-md flex items-center justify-center py-2 font-medium hover:bg-black-50"
        data-testid="clear-filters-main-button"
      >
        Clear Filters
      </button>
    </div>
  );
};

const SectionComponent: FilterSectionsSectionComponent = ({ section }) => (
  <DesktopFilterSection section={section}>
    {RANGE_FILTER_SECTIONS.has(section) ? (
      <InventoryFiltersRange section={section} />
    ) : (
      <FilterSectionOptionsContent section={section} />
    )}
  </DesktopFilterSection>
);

const TagComponent: FilterSectionsTagComponent = ({ item }) => (
  <DesktopFilterSection key={item.name} section={item.name}>
    <OptionsSectionContent options={[]} section={item.name} value={[]} toggleOption={() => {}} />
  </DesktopFilterSection>
);

export const DesktopFilters = () => (
  <div className="flex flex-col font-helvetica text-black-500 gap-5">
    <h2 className="font-bold text-xl mb-3">Filters</h2>

    <FilterList />

    <SearchInput />

    <FilterSections sectionComponent={SectionComponent} tagComponent={TagComponent} />
  </div>
);
